import dayjs from 'dayjs';
import { useEffect } from 'react';

import VPeriodSelectorContext from './context';
import type { RangePivot, VPeriodSelectorContextValue } from './type';
import type { PeriodUnit } from './type';
import { getMonthWeekInfo } from './util';

export const useVPeriodSelectorContext = <
  T extends boolean,
>(): VPeriodSelectorContextValue<T> =>
  VPeriodSelectorContext.hook() as VPeriodSelectorContextValue<T>;

export const useInitializeDateRangeOnPivotChange = ({
  rangePivot,
  onDateRangeChange,
}: {
  rangePivot: RangePivot;
  onDateRangeChange: (dateRange: [DateLike, DateLike]) => void;
}) => {
  useEffect(() => {
    switch (rangePivot.unit) {
      case 'custom': {
        break;
      }
      case 'week': {
        const toSubtractValue = rangePivot.value;
        const { thisWeekMonday, thisWeekSunday } = getMonthWeekInfo(dayjs());
        onDateRangeChange([
          thisWeekMonday.subtract(toSubtractValue - 1, 'week').toDate(),
          thisWeekSunday.toDate(),
        ]);
        break;
      }
      default: {
        const { unit, value } = rangePivot;
        onDateRangeChange([
          dayjs().subtract(value, unit).toDate(),
          dayjs().toDate(),
        ]);
        break;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rangePivot]);
};

export const useDateNavigation = () => {
  const navigateDateRange = (
    dateRange: [DateLike, DateLike],
    periodUnit: PeriodUnit,
    direction: 'prev' | 'next',
  ): [Date, Date] => {
    const [start, end] = dateRange;
    const offset = direction === 'prev' ? -1 : 1;

    return [
      dayjs(start).add(offset, periodUnit).toDate(),
      dayjs(end).add(offset, periodUnit).toDate(),
    ];
  };

  const handleDatePickerChange = (
    val: [DateLike, DateLike],
    periodUnit: PeriodUnit,
    onDateRangeChange: (dateRange: [DateLike, DateLike]) => void,
  ) => {
    const [_start, _end] = val;
    const start = dayjs(_start);
    const end = dayjs(_end);

    if (periodUnit === 'week') {
      const { thisWeekMonday: startWeekMonday } = getMonthWeekInfo(start);
      const { thisWeekSunday: endWeekSunday } = getMonthWeekInfo(end);

      onDateRangeChange([startWeekMonday.toDate(), endWeekSunday.toDate()]);
    } else {
      onDateRangeChange([start.toDate(), end.toDate()]);
    }
  };

  const canNavigateNext = (
    dateRange: [DateLike, DateLike],
    periodUnit: PeriodUnit,
  ): boolean => {
    const [start] = dateRange;
    return !dayjs(start).add(1, periodUnit).isAfter(dayjs());
  };

  return {
    navigateDateRange,
    handleDatePickerChange,
    canNavigateNext,
  };
};
